/** اعداد با جداکننده هزارگان (٬) — ورودی و نمایش */

const PERSIAN_DIGITS = '۰۱۲۳۴۵۶۷۸۹';

const toPersian = (s: string) => s.replace(/\d/g, (d) => PERSIAN_DIGITS[parseInt(d, 10)]);

const withThousands = (intPart: string) => intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '٬');

const toNumber = (n: string | number | null | undefined): number | null => {
  if (n == null || n === '') return null;
  const num = typeof n === 'string' ? parseFloat(String(n).replace(/[,٬\s]/g, '')) : n;
  return Number.isNaN(num) ? null : num;
};

export const parseNumberInput = (s: string): string => {
  const cleaned = s.replace(/[,٬\s]/g, '').replace(/[۰-۹]/g, (d) => String(PERSIAN_DIGITS.indexOf(d)));
  const match = cleaned.match(/^\d*\.?\d*/);
  return match ? match[0] : '';
};

/** فقط عدد صحیح (برای مبالغ ریالی) */
export const parseMoneyInput = (s: string): string => {
  const cleaned = s.replace(/[,٬\s]/g, '').replace(/[۰-۹]/g, (d) => String(PERSIAN_DIGITS.indexOf(d)));
  const match = cleaned.match(/^\d*/);
  return match ? match[0] : '';
};

export const formatNumberInput = (s: string): string => {
  if (!s) return '';
  const parsed = parseNumberInput(s);
  if (!parsed) return '';
  const [intPart, decPart] = parsed.split('.');
  const formatted = withThousands(intPart);
  if (decPart === undefined) return formatted;
  const decTrimmed = decPart.replace(/0+$/, '');
  if (!decTrimmed) return formatted;
  return `${formatted}.${decTrimmed}`;
};

/** ورودی مبلغ بدون اعشار */
export const formatMoneyInput = (s: string): string => {
  if (!s) return '';
  const parsed = parseMoneyInput(s);
  if (!parsed) return '';
  return withThousands(parsed);
};

export const formatRawNumber = (n: string | number | null | undefined): string => {
  if (n == null || n === '') return '';
  const cleaned = String(n).replace(/[,٬\s]/g, '');
  if (!cleaned) return '';
  return formatNumberInput(cleaned);
};

export const parseNumberToFloat = (s: string): number => {
  const parsed = parseNumberInput(s);
  if (!parsed) return 0;
  const num = parseFloat(parsed);
  return Number.isNaN(num) ? 0 : num;
};

/**
 * نمایش مبلغ ریالی — همیشه بدون اعشار (رند به نزدیک‌ترین عدد صحیح)
 */
export const formatMoney = (n: string | number | null | undefined): string => {
  const num = toNumber(n);
  if (num == null) return '۰';
  const rounded = Math.round(num);
  const sign = rounded < 0 ? '−' : '';
  const abs = Math.abs(rounded);
  return sign + toPersian(withThousands(String(abs)));
};

const compactUnit = (abs: number, unit: number, name: string): string => {
  const v = abs / unit;
  const rounded = v >= 100 ? v.toFixed(0) : v.toFixed(2).replace(/\.?0+$/, '');
  return `${toPersian(rounded)} ${name}`;
};

/** نمایش فشرده برای موبایل: میلیون / میلیارد / همت */
export const formatCompactMoney = (n: string | number | null | undefined): string => {
  const num = toNumber(n);
  if (num == null) return '۰';
  const sign = num < 0 ? '−' : '';
  const abs = Math.abs(num);
  if (abs >= 1e12) return sign + compactUnit(abs, 1e12, 'همت');
  if (abs >= 1e9) return sign + compactUnit(abs, 1e9, 'میلیارد');
  if (abs >= 1e6) return sign + compactUnit(abs, 1e6, 'میلیون');
  return formatMoney(num);
};

/** @deprecated از formatMoney استفاده کنید — همان رفتار بدون اعشار */
export const formatDisplayNumber = (n: string | number | null | undefined): string => {
  if (n == null || n === '') return '—';
  const num = toNumber(n);
  if (num == null) return String(n);
  return formatMoney(num);
};

/**
 * مقدار/تعداد — حداکثر ۲ رقم اعشار (صفرهای انتهایی حذف می‌شود)
 */
export const formatQuantity = (n: string | number | null | undefined, maxDecimals = 2): string => {
  const num = toNumber(n);
  if (num == null) return '۰';
  const rounded = Number(num.toFixed(maxDecimals));
  const [intPart, decPart] = rounded.toString().split('.');
  const formatted = withThousands(intPart.replace('-', ''));
  const sign = rounded < 0 ? '−' : '';
  if (!decPart) return sign + toPersian(formatted);
  return sign + toPersian(`${formatted}.${decPart}`);
};

/**
 * درصد — حداکثر ۲ رقم اعشار برای نمایش تمیز
 */
export const formatPercentDisplay = (n: string | number | null | undefined, maxDecimals = 2): string => {
  const num = toNumber(n);
  if (num == null) return '۰';
  const rounded = Number(num.toFixed(maxDecimals));
  const isApprox = Math.abs(rounded - num) > 1e-10;
  return (isApprox ? '≈' : '') + toPersian(rounded.toString());
};
